/**
 * Price maths shared by the API and the chart.
 *
 * The stop-loss evaluation on the server and the distance leg of a horizontal
 * line in the browser must agree to the last digit, so both call these functions
 * rather than reimplementing the formula.
 */
/** Distance from a reference price (usually the latest close) to a target level. */
export const priceDistance = (reference, target) => {
    const absolute = reference - target;
    const percent = reference > 0 ? (absolute / reference) * 100 : null;
    return { absolute, percent };
};
/**
 * Change from an earlier price to a later one: `to - from`.
 *
 * The counterpart to `priceDistance`, which measures *downwards* from a reference.
 * Having both named explicitly avoids sign flips at call sites, where a stray
 * minus is easy to write and hard to spot.
 */
export const priceChange = (from, to) => {
    const absolute = to - from;
    const percent = from > 0 ? (absolute / from) * 100 : null;
    return { absolute, percent };
};
/** Formats a price for axis labels and tables. */
export const formatPrice = (value, options = {}) => {
    const { currency, locale = 'de-DE', digits } = options;
    const fractionDigits = digits ?? (Math.abs(value) < 1 ? 4 : 2);
    const formatter = new Intl.NumberFormat(locale, {
        style: currency ? 'currency' : 'decimal',
        currency: currency ?? undefined,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });
    return formatter.format(value);
};
/**
 * Formatiert einen Prozentwert, standardmäßig mit ausdrücklichem Vorzeichen
 * (`-12,40 %`).
 *
 * `showSign: false` unterdrückt es. Gebraucht wird das, wo das Vorzeichen bereits
 * im Text steckt: „15,54 % entfernt" ist eine Entfernung, und ein Minuszeichen
 * davor liest sich wie eine negative Entfernung.
 */
export const formatPercent = (value, options = {}) => {
    const { locale = 'de-DE', digits = 2, showSign = true } = options;
    const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        signDisplay: showSign ? 'exceptZero' : 'never',
    }).format(value);
    return `${formatted} %`;
};
/**
 * Formatiert einen Betrag mit ausdrücklichem Vorzeichen, etwa `+1,23 €`.
 *
 * Mit `currency` wird das Währungszeichen gesetzt statt des Codes: „+942,46 €"
 * liest sich schneller als „+942,46 EUR" und bricht nicht so leicht um.
 */
export const formatSigned = (value, options = {}) => {
    const { currency, locale = 'de-DE', digits = 2 } = options;
    return new Intl.NumberFormat(locale, {
        style: currency ? 'currency' : 'decimal',
        currency: currency ?? undefined,
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        signDisplay: 'exceptZero',
    }).format(value);
};
/** Applies a percentage discount, e.g. 10 % below 100 -> 90. */
export const applyPercentBelow = (price, percent) => price * (1 - percent / 100);
//# sourceMappingURL=price.js.map